import React, { useState } from 'react';
import { Crown, Users, Upload, RefreshCw, X, Star, Gift, Zap, Download, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import AuthModal from './AuthModal';
import { useAuth } from '../contexts/AuthContext';

const LoginPrompt = ({ 
  trigger = 'default',
  onDismiss = null,
  compact = false,
  className = ''
}) => {
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't show if user is already authenticated or prompt is dismissed
  if (isAuthenticated || dismissed) {
    return null;
  }

  const prompts = {
    default: {
      icon: <Crown className="h-6 w-6 text-yellow-500" />,
      title: "Unlock Premium LA Adventures",
      description: "Join 10,000+ explorers discovering hidden gems with premium walks, gear guides, and exclusive content!",
      ctaText: "Start Free Trial",
      secondaryText: "Browse Free Content",
      prompt: "default",
      gradient: "from-yellow-50 via-orange-50 to-red-50",
      border: "border-yellow-300",
      highlights: ["100+ Premium Walks", "Expert Gear Reviews", "Offline Maps & GPX"],
      badge: "🔥 Most Popular",
      memberCount: "10,000+"
    },
    syncLists: {
      icon: <Download className="h-6 w-6 text-blue-500" />,
      title: "Never Lose Your Perfect Walks",
      description: "Sync your curated walk collections across all devices and unlock unlimited list creation with premium features!",
      ctaText: "Upgrade to Premium",
      secondaryText: "Continue as Guest",
      prompt: "syncLists",
      gradient: "from-blue-50 via-indigo-50 to-purple-50",
      border: "border-blue-300",
      highlights: ["Unlimited Lists", "Cloud Sync", "Advanced Filters"],
      badge: "⚡ Instant Access",
      memberCount: "10,000+"
    },
    premium: {
      icon: <Star className="h-6 w-6 text-purple-500" />,
      title: "Unlock the Full LA Experience",
      description: "Access secret spots, seasonal collections, expert gear reviews, and join an exclusive community of urban explorers!",
      ctaText: "Start 7-Day Free Trial",
      secondaryText: "View Free Walks",
      prompt: "premium",
      gradient: "from-purple-50 via-pink-50 to-rose-50",
      border: "border-purple-300",
      highlights: ["Secret LA Spots", "Gear Discounts", "VIP Community"],
      badge: "👑 Premium Access",
      memberCount: "10,000+"
    },
    submit: {
      icon: <Gift className="h-6 w-6 text-green-500" />,
      title: "Share & Earn Premium Access",
      description: "Submit your favorite walks and earn premium credits! Help fellow explorers while unlocking exclusive features.",
      ctaText: "Join & Submit",
      secondaryText: "Learn More",
      prompt: "submit",
      gradient: "from-green-50 via-emerald-50 to-teal-50",
      border: "border-green-300",
      highlights: ["Earn Premium Credits", "Community Recognition", "Featured Submissions"],
      badge: "🎁 Earn Rewards",
      memberCount: "10,000+"
    },
    community: {
      icon: <Users className="h-6 w-6 text-indigo-500" />,
      title: "Join LA's Premier Walking Community",
      description: "Connect with 10,000+ urban explorers, access exclusive group walks, and discover your next adventure partner!",
      ctaText: "Join Community",
      secondaryText: "Explore Solo",
      prompt: "community",
      gradient: "from-indigo-50 via-blue-50 to-cyan-50",
      border: "border-indigo-300",
      highlights: ["Group Walks", "Local Meetups", "Explorer Network"],
      badge: "🌟 Community",
      memberCount: "10,000+"
    }
  };

  const config = prompts[trigger] || prompts.default;

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  const handleSecondaryAction = () => {
    if (trigger === 'syncLists') {
      handleDismiss();
    } else if (trigger === 'premium') {
      window.location.href = '/walks';
    } else if (trigger === 'submit') {
      window.location.href = '/about';
    } else if (trigger === 'community') {
      handleDismiss();
    } else {
      window.location.href = '/walks';
    }
  };

  if (compact) {
    return (
      <>
        <Card className={`bg-gradient-to-r ${config.gradient} ${config.border} shadow-lg hover:shadow-xl transition-all duration-300 ${className}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-sm">
                  {config.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-semibold px-2 py-0.5">
                      {config.badge}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-sm text-gray-900">{config.title}</h3>
                  <p className="text-xs text-gray-700">Join {config.memberCount} explorers</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  onClick={() => setShowAuthModal(true)}
                  className="text-xs bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg"
                >
                  {config.ctaText}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleDismiss}
                  className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="signup"
          prompt={config.prompt}
        />
      </>
    );
  }

  return (
    <>
      <Card className={`bg-gradient-to-br ${config.gradient} ${config.border} shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02] ${className}`}>
        <CardContent className="p-8">
          <div className="flex items-start gap-6">
            {/* Icon with animated glow */}
            <div className="w-16 h-16 bg-white/95 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg border-2 border-white/50">
              {config.icon}
            </div>
            
            <div className="flex-1">
              {/* Top badges and social proof */}
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-bold px-3 py-1 shadow-md">
                  {config.badge}
                </Badge>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-gray-700">4.9/5</span>
                  <span className="text-sm text-gray-600">• Trusted by {config.memberCount} walkers</span>
                </div>
              </div>
              
              {/* Main content */}
              <h3 className="font-black text-2xl mb-3 text-gray-900 leading-tight">{config.title}</h3>
              <p className="text-gray-800 mb-6 text-lg leading-relaxed font-medium">{config.description}</p>
              
              {/* Feature highlights */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {config.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/50">
                    <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-blue-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-800">{highlight}</span>
                  </div>
                ))}
              </div>
              
              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <Button 
                  onClick={() => setShowAuthModal(true)}
                  className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 text-white font-bold py-3 px-8 text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                  size="lg"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  {config.ctaText}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleSecondaryAction}
                  className="bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 font-semibold border-2 border-white/70 hover:border-gray-300 py-3 px-6 text-lg transition-all duration-300"
                  size="lg"
                >
                  {config.secondaryText}
                </Button>
              </div>
              
              {/* Value proposition */}
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-gray-700">7-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-gray-700">Cancel anytime</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-600" />
                  <span className="font-semibold text-gray-700">Instant access</span>
                </div>
              </div>
            </div>
            
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-full transition-all duration-300"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="signup"
        prompt={config.prompt}
      />
    </>
  );
};

export default LoginPrompt;